import './browse-dialog.scss';
import '@jetbrains/ring-ui/components/dialog-ng/dialog-ng';
import indexOf from 'mout/array/indexOf';
import map from 'mout/array/map';
import some from 'mout/array/some';
import ExternalValidator from '../external-validator/external-validator';
import InstallerResource from '../resource/resource';
import Utils from './browse-dialog__utils';

const ARROW_DOWN = 40;
const ENTER = 13;
const ARROW_UP = 38;

export default angular.module('installer.browse-dialog', ['Ring.dialog', InstallerResource.name, ExternalValidator.name]).
  controller('BrowseDialogCtrl', function controller($q, $scope, $element, i18n, installerResource, dialog) {
    const ctrl = this;

    const dialogData = $scope.data;
    ctrl.allowFiles = dialogData.allowFiles;
    let utils = null;
    ctrl.visiblePath = null;
    ctrl.breadcrumbs = [];
    ctrl.pathError = null;
    ctrl.pathInformation = null;
    ctrl.children = [];
    ctrl.focused = null;
    ctrl.pathInputNode = $element[0].querySelector('.js-path-input');

    function getBreadcrumbsUrlParts(toIndex = ctrl.breadcrumbs.length - 1) {
      return map(ctrl.breadcrumbs.slice(0, toIndex + 1), breadcrumb => breadcrumb.path);
    }

    dialogData.getPath = () => utils.addTrailingSlashIfNeeded(ctrl.visiblePath);

    dialogData.setDefaultValue = value => {
      ctrl.visiblePath = utils.addTrailingSlashIfNeeded(dialogData.defaultValue || value);
    };

    dialogData.setPathError = error => {
      ctrl.pathError = error;
    };

    function updateRevertDefaultButtonVisibility(newPath) {
      //Hack for hiding button in dialog-ng
      const resetToDefaultButton = document.querySelector('[data-test="dialog-footer-button"]:nth-child(2)');

      resetToDefaultButton.style.display = dialogData.defaultValue === newPath ? 'none' : 'inline-block';
    }

    ctrl.getErrorMessage = (response, path) => {
      if (response.numOfRoots === 0) {
        return i18n('There is no drives to select. Please check your file system');
      }

      if (utils.notAbsolutePath(response)) {
        return i18n('Only absolute path is valid');
      }

      const unavailableDiskName = utils.getUnavailableDisk(response.path, path);
      if (unavailableDiskName) {
        return i18n(`Disk ${unavailableDiskName} is not writable`);
      }

    };

    ctrl.getInformationMessage = (response, path) => {
      const containsUnexist = some(ctrl.breadcrumbs, b => !b.exists);
      if (containsUnexist) {
        if (ctrl.allowFiles) {
          return i18n('Path does not exist.');
        }
        return i18n('Some folders in the selected path do not exist and will be created during installation.');
      }
      if (!utils.endsWithSeparator(path) && !response.children.length) {
        const lastPathPart = utils.getPathParts(path)[utils.getPathParts(path).length - 1];
        return i18n(`There are no files or subfolders matched "${lastPathPart}"`);
      }
      if (!response.children.length) {
        return i18n('There are no files or sub folders to select here.');
      }
    };

    ctrl.isFile = name => !utils.endsWithSeparator(name);

    const browsePath = path => {
      // eslint-disable-next-line no-param-reassign
      path = path || '';
      return installerResource.browse.get({path, showFiles: ctrl.allowFiles}).$promise.
        catch(err => {
          if (err.status === -1) {
            dialog.hide();
          }
          return $q.reject(err);
        }).
        then(response => {
          const shouldUseRoots = !response.path && response.children.length === 0;
          ctrl.children = shouldUseRoots ? response.roots : response.children;
          ctrl.breadcrumbs = utils.getRootBreadcrumb(response.numOfRoots);
          return response;
        }).
        then(response => {
          let clientPathParts = utils.getPathParts(path);
          const serverPathParts = utils.getPathParts(response.path || '');

          /**
           * Fill clientPath by server, if it is empty
           */
          if (!clientPathParts.length) {
            clientPathParts = serverPathParts;
          }

          for (let i = 0; i < clientPathParts.length; i++) {
            /**
             * Do not add last part to breadcrumbs if there is no trailing slash
             */
            if (path && !utils.endsWithSeparator(path) && i === clientPathParts.length - 1) {
              break;
            }

            ctrl.breadcrumbs.push({
              name: clientPathParts[i],
              path: clientPathParts[i],
              exists: utils.isSameParts(clientPathParts[i], serverPathParts[i])
            });
          }

          if (ctrl.breadcrumbs.some(b => !b.exists)) {
            ctrl.children = [];
          }

          return response;
        }).
        then(response => {
          ctrl.pathError = ctrl.getErrorMessage(response, path);
          ctrl.pathInformation = ctrl.getInformationMessage(response, path);

          //If we are on unix, and path (and startPath) is empty - open root folder
          if (!dialogData.value && path === '' && response.separator === '/') {
            browsePath(response.separator);
          }
        });
    };

    ctrl.selectItem = item => {
      let previousPath = getBreadcrumbsUrlParts();
      //hack for unix root selection
      if (previousPath.length === 1 && previousPath[0] === '/' && item[0] === '/') {
        previousPath = [];
      }

      if (ctrl.isFile(item)) {
        ctrl.visiblePath = utils.constructPathByParts(previousPath.concat(item));
      } else {
        ctrl.focused = null;
        ctrl.visiblePath = utils.constructPathByParts(previousPath.concat(item));
      }

      if (ctrl.pathInputNode) {
        ctrl.pathInputNode.focus();
      }
    };

    ctrl.breadcrumbClick = index => {
      ctrl.visiblePath = utils.constructPathByParts(getBreadcrumbsUrlParts(index));
    };

    const scrollActiveItemIntoView = selector => {
      const el = document.querySelector(selector);
      if (!el) {
        return;
      }
      if (el.scrollIntoViewIfNeeded) {
        return el.scrollIntoViewIfNeeded();
      }
      el.scrollIntoView({block: 'end', behavior: 'smooth'});
    };

    ctrl.handleKey = e => {
      let selectedIndex = indexOf(ctrl.children, ctrl.focused);

      if (e.keyCode === ENTER && ctrl.focused) {
        if (!this.isFileSelected(ctrl.focused)) {
          ctrl.selectItem(ctrl.focused);
          e.stopPropagation();
          return false;
        } else {
          return true;
        }
      }

      if (e.keyCode === ARROW_DOWN) {
        selectedIndex++;
        if (!ctrl.children[selectedIndex]) {
          selectedIndex = 0;
        }
      }

      if (e.keyCode === ARROW_UP) {
        selectedIndex--;
        if (!ctrl.children[selectedIndex]) {
          selectedIndex = ctrl.children.length - 1;
        }
      }

      ctrl.focused = ctrl.children[selectedIndex];

      setTimeout(() => scrollActiveItemIntoView('[data-scroll-to-focused="true"]'), 10);
    };

    ctrl.isFileSelected = file => utils && utils.isFilePath(file) && utils.getPathParts(this.visiblePath).pop() === file;

    function activate() {
      ctrl.visiblePath = dialogData.value;
      let initialFocus = false;

      $scope.$watch('ctrl.visiblePath', newValue => {
        ctrl.pathError = null;
        ctrl.pathInformation = null;

        updateRevertDefaultButtonVisibility(newValue);
        let promise = null;

        if (utils && utils.isFilePath(newValue)) {
          promise = browsePath(newValue.replace(utils.getPathParts(newValue).pop(), ''));
        } else {
          promise = browsePath(newValue);
        }

        !initialFocus && promise.then(() => {
          initialFocus = true;
          setTimeout(() => scrollActiveItemIntoView('[data-scroll-to-selected="true"]'), 10);
        });
      });
    }

    installerResource.browse.get({path: ''}).$promise.
      then(response => {
        utils = new Utils(response.separator);
        activate();
      });
  });



// WEBPACK FOOTER //
// ./src/blocks/browse-dialog/browse-dialog.js